/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: sms/pages/SmsProvidersPage.tsx
 🗓️ Created: 2026-01-20
 🧩 Description:
    SMS Providers management page - following Extensions module pattern.
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, Plus, Edit, Trash2, TestTube2 } from 'lucide-react';
import { ROUTES } from '../../../constants/routes';
import { useI18n } from '../../../hooks/useI18n';
import DataGridSimple from '../../../components/ui/DataGridSimple';
import { useNotifications } from '../../../contexts/NotificationContext';
import ConfirmationModal from '../../../components/ui/ConfirmationModal';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { useSmsProvidersList } from '../hooks/useSmsProvidersList';
import { deleteProvider, testProvider } from '../api';
import type { SmsProvider } from '../types';

// Provider type display names
const PROVIDER_LABELS: Record<string, string> = {
    twilio: 'Twilio',
    telnyx: 'Telnyx',
    signalwire: 'SignalWire',
    vonage: 'Vonage',
    bandwidth: 'Bandwidth',
    bulkvs: 'BulkVS',
    skyetel: 'Skyetel',
    voipms: 'VoIP.ms',
    didww: 'DIDWW',
    commio: 'Commio',
    voxtelesys: 'Voxtelesys',
    questblue: 'QuestBlue',
    wavix: 'Wavix',
    voipinnovations: 'VoIP Innovations',
    flowroute: 'Flowroute',
    wayinteractive: 'Way Interactive',
    custom: 'Custom',
};

interface ProviderColumn {
    key: string;
    label: string;
    sortable: boolean;
    visible: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    render?: (value: any, item: SmsProvider) => React.ReactNode;
}

export default function SmsProvidersPage() {
    const navigate = useNavigate();
    const { t, i18n } = useI18n();
    const { showSuccess, showError } = useNotifications();
    const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; provider: SmsProvider | null }>({
        isOpen: false,
        provider: null,
    });
    const [testingId, setTestingId] = useState<number | null>(null);

    // Define columns
    const availableColumns = useMemo<ProviderColumn[]>(() => [
        {
            key: 'name',
            label: t('sms.providers.columns.name'),
            sortable: true,
            visible: true,
            render: (_value: unknown, item: SmsProvider) => (
                <span className="font-medium text-primary">{item?.name || '-'}</span>
            ),
        },
        {
            key: 'providerType',
            label: t('sms.providers.columns.type'),
            sortable: true,
            visible: true,
            render: (_value: unknown, item: SmsProvider) => (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400">
                    {PROVIDER_LABELS[item?.providerType] || item?.providerType || '-'}
                </span>
            ),
        },
        {
            key: 'priority',
            label: t('sms.providers.columns.priority'),
            sortable: true,
            visible: true,
            render: (_value: unknown, item: SmsProvider) => item?.priority || '-',
        },
        {
            key: 'isDefault',
            label: t('sms.providers.columns.default'),
            sortable: false,
            visible: true,
            render: (_value: unknown, item: SmsProvider) => (
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${item?.isDefault
                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400'
                    }`}>
                    {item?.isDefault ? '✓' : '-'}
                </span>
            ),
        },
        {
            key: 'enabled',
            label: t('sms.providers.columns.enabled'),
            sortable: true,
            visible: true,
            render: (_value: unknown, item: SmsProvider) => (
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${item?.enabled
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                    }`}>
                    {item?.enabled ? t('common.enabled') : t('common.disabled')}
                </span>
            ),
        },
        {
            key: 'actions',
            label: t('common.actions'),
            sortable: false,
            visible: true,
        },
    ], [t, i18n.language]);

    // Use custom hook for list management
    const {
        data,
        loading,
        columns,
        pagination,
        searchQuery,
        sortBy,
        sortOrder,
        handleSearch,
        handleSort,
        handlePageChange,
        handleItemsPerPageChange,
        handleColumnToggle,
        refresh,
    } = useSmsProvidersList({
        initialColumns: availableColumns,
        initialSortBy: 'name',
        initialSortOrder: 'asc',
        initialLimit: 10,
    });

    // Handlers
    const handleCreate = useCallback(() => {
        navigate(`${ROUTES.SMS_PROVIDERS}/new`);
    }, [navigate]);

    const handleEdit = useCallback((provider: SmsProvider) => {
        const idToUse = provider.uuid ?? provider.id;
        navigate(ROUTES.SMS_PROVIDER_DETAIL(idToUse));
    }, [navigate]);

    const handleDelete = useCallback((provider: SmsProvider) => {
        setDeleteModal({ isOpen: true, provider });
    }, []);

    const confirmDelete = useCallback(async () => {
        if (!deleteModal.provider) return;

        try {
            const idToUse = deleteModal.provider.uuid ?? deleteModal.provider.id;
            await deleteProvider(idToUse);
            showSuccess(t('sms.providers.messages.deleteSuccess'));
            setDeleteModal({ isOpen: false, provider: null });
            refresh();
        } catch (error) {
            console.error('Error deleting provider:', error);
            showError(error.message || t('sms.providers.messages.deleteError'));
        }
    }, [deleteModal.provider, refresh, showError, showSuccess, t]);

    const handleTest = useCallback(async (provider: SmsProvider) => {
        setTestingId(provider.id);
        try {
            const result = await testProvider(provider.id);
            if (result.connected) {
                showSuccess(t('sms.providers.messages.testSuccess', { latency: result.latencyMs }));
            } else {
                showError(t('sms.providers.messages.testFailed'));
            }
        } catch (error) {
            showError(error.message || t('sms.providers.messages.testError'));
        } finally {
            setTestingId(null);
        }
    }, [showError, showSuccess, t]);

    // Render actions for each row
    const renderActions = useCallback((provider: SmsProvider) => (
        <div className="flex items-center gap-2">
            <SimpleTooltip content={t('sms.providers.actions.test')} position="top">
                <button
                    onClick={() => handleTest(provider)}
                    disabled={testingId === provider.id}
                    className="p-2 hover:bg-purple-100 dark:hover:bg-purple-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60 disabled:opacity-50"
                >
                    <TestTube2 className={`h-4 w-4 text-purple-600 dark:text-purple-400 ${testingId === provider.id ? 'animate-pulse' : ''}`} />
                </button>
            </SimpleTooltip>

            <SimpleTooltip content={t('common.edit')} position="top">
                <button
                    onClick={() => handleEdit(provider)}
                    className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
                >
                    <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                </button>
            </SimpleTooltip>

            <SimpleTooltip content={t('common.delete')} position="top">
                <button
                    onClick={() => handleDelete(provider)}
                    className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
                >
                    <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
                </button>
            </SimpleTooltip>
        </div>
    ), [t, testingId, handleEdit, handleTest, handleDelete]);

    return (
        <div className="p-6">
            <DataGridSimple
                data={data}
                loading={loading}
                columns={columns}
                pagination={pagination}
                searchQuery={searchQuery}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSearch={handleSearch}
                onSort={handleSort}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                onColumnToggle={handleColumnToggle}
                renderActions={renderActions}
                searchPlaceholder={t('sms.providers.searchPlaceholder')}
                title={t('sms.providers.title')}
                titleIcon={<MessageSquare className="h-5 w-5 text-primary" />}
                actions={
                    <div className="flex items-center gap-2">
                        <SimpleTooltip content={t('sms.providers.actions.add')} position="top">
                            <button
                                onClick={handleCreate}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm">
                                <Plus className="h-4 w-4" /> {t('sms.providers.actions.add')}
                            </button>
                        </SimpleTooltip>
                    </div>
                }
            />

            <ConfirmationModal
                isOpen={deleteModal.isOpen}
                onClose={() => setDeleteModal({ isOpen: false, provider: null })}
                onConfirm={confirmDelete}
                title={t('sms.providers.deleteTitle')}
                message={`${t('sms.providers.messages.confirmDelete')} (${deleteModal.provider?.name})`}
                confirmText={t('common.delete')}
                cancelText={t('common.cancel')}
                type="danger"
            />
        </div>
    );
}
